import React, { useState, useEffect } from 'react';
import axios, { API } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Rocket } from 'lucide-react';

const StartHereDialog = ({ autoOpen = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (autoOpen) {
      setIsOpen(true);
    }
  }, [autoOpen]);

  const handleClose = async () => {
    setIsOpen(false);
    if (autoOpen) {
      try {
        await axios.post(`${API}/auth/onboarding-seen`);
      } catch (error) {
        console.error('Failed to mark onboarding as seen:', error);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); else setIsOpen(true); }}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="text-justb-primary border-justb-primary/30 hover:bg-justb-primary/5 font-medium"
        data-testid="start-here-btn"
      >
        <Rocket className="h-4 w-4 mr-1.5" />
        Start Here
      </Button>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden" aria-describedby="start-here-description">
        <DialogHeader>
          <DialogTitle className="text-justb-primary font-semibold text-xl">Welcome to Just B. Goal Tracker</DialogTitle>
        </DialogHeader>
        <div id="start-here-description" className="overflow-y-auto max-h-[70vh] space-y-4 text-gray-900 pr-2">
          <div>
            <h3 className="font-semibold text-lg text-justb-secondary mb-2">What is Just B.?</h3>
            <p className="text-sm leading-relaxed">
              Just B. is an AI-powered life goal management app designed to help you transform your aspirations into achievements.
              It breaks down your goals into manageable daily tasks and provides structured accountability through reflections and progress tracking.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-lg text-justb-secondary mb-2">Key Features</h3>
            <ul className="text-sm space-y-1 ml-4">
              <li className="flex items-start"><span className="text-justb-primary mr-2">&bull;</span><span><strong>AI Task Generation:</strong> Creates personalized daily tasks based on your goal details</span></li>
              <li className="flex items-start"><span className="text-justb-primary mr-2">&bull;</span><span><strong>Daily Reflections:</strong> Track your learning and progress</span></li>
              <li className="flex items-start"><span className="text-justb-primary mr-2">&bull;</span><span><strong>AI Coach:</strong> Get honest, warm feedback on your reflections</span></li>
              <li className="flex items-start"><span className="text-justb-primary mr-2">&bull;</span><span><strong>Goal Extensions:</strong> Extend deadlines with accountability</span></li>
              <li className="flex items-start"><span className="text-justb-primary mr-2">&bull;</span><span><strong>Archive Management:</strong> Pause and restore goals anytime</span></li>
              <li className="flex items-start"><span className="text-justb-primary mr-2">&bull;</span><span><strong>Progress Reports:</strong> Detailed journey tracking</span></li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-lg text-justb-secondary mb-2">How to Get Started</h3>
            <ol className="text-sm space-y-1 ml-4">
              <li className="flex items-start"><span className="text-justb-primary mr-2 font-medium">1.</span><span>Click <strong>"+ New Goal"</strong> and provide a clear, specific title</span></li>
              <li className="flex items-start"><span className="text-justb-primary mr-2 font-medium">2.</span><span>Write a detailed description — the more detail, the better the AI tasks</span></li>
              <li className="flex items-start"><span className="text-justb-primary mr-2 font-medium">3.</span><span>Set a realistic target date for completion</span></li>
              <li className="flex items-start"><span className="text-justb-primary mr-2 font-medium">4.</span><span>Complete daily tasks and add reflections for AI coaching</span></li>
              <li className="flex items-start"><span className="text-justb-primary mr-2 font-medium">5.</span><span>Review your progress in the <strong>Reports</strong> tab</span></li>
            </ol>
          </div>
          <div className="bg-justb-primary/10 p-4 rounded-lg">
            <p className="text-sm text-justb-secondary">
              <strong>Pro Tip:</strong> After completing a task and writing a reflection, click <strong>"Get AI feedback"</strong> for personalized coaching from your AI accountability partner.
            </p>
          </div>
        </div>
        <div className="flex justify-end mt-4 pt-4 border-t">
          <Button onClick={handleClose} className="bg-justb-primary hover:bg-justb-primary/90" data-testid="start-here-close-btn">
            Let's go!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StartHereDialog;
